/** Bounded identity checks for an explicitly selected local-library record. */
export type LocalFileIdentity = {
  id: string; sourceType: string; fileName?: string; externalId?: string;
  size?: number; durationMs?: number; metadata?: Record<string, any>;
};
const leaf = (path: string) => path.replace(/\\/g, '/').split('/').pop()!.normalize('NFC');
const relativePath = (path: string) => path.replace(/\\/g, '/').normalize('NFC');

// This is a sampled signature, not a hash of the complete audio. Reads at most
// three 64-KiB slices and is never used to search or scan the user's disk.
export async function localFileSampleSignature(file: Blob): Promise<string> {
  const length = 64 * 1024;
  const offsets = [...new Set([0, Math.max(0, Math.floor((file.size - length) / 2)), Math.max(0, file.size - length)])];
  const header = new TextEncoder().encode(`sample-v1:${file.size}:${offsets.join(',')}\n`);
  const samples = await Promise.all(offsets.map(offset => file.slice(offset, offset + length).arrayBuffer()));
  const bytes = new Uint8Array(header.length + samples.reduce((total, sample) => total + sample.byteLength, 0));
  bytes.set(header); let cursor = header.length;
  for (const sample of samples) { bytes.set(new Uint8Array(sample), cursor); cursor += sample.byteLength; }
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return `sample-v1:${file.size}:${[...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, '0')).join('')}`;
}

export function localFileRecoveryError(track: LocalFileIdentity, file: { name: string; size: number; webkitRelativePath?: string }, durationMs: number | undefined, signature: string, tracks: LocalFileIdentity[]): string {
  const mismatch = '所选文件与原歌曲不匹配，请重新选择原文件';
  const name = leaf(track.fileName || track.externalId || '');
  if (!name || leaf(file.name) !== name || !Number.isFinite(track.size) || file.size !== track.size) return mismatch;
  const selectedPath = relativePath(file.webkitRelativePath?.trim() || '');
  const originalPath = relativePath(track.fileName || track.externalId || '');
  if (selectedPath && selectedPath !== originalPath) return mismatch;
  if (!Number.isFinite(track.durationMs) || !Number.isFinite(durationMs) || Math.abs(track.durationMs! - durationMs!) >= 1000) return mismatch;
  const expected = track.metadata?.localFileSampleSignature;
  if (typeof expected === 'string' && expected) return expected === signature ? '' : mismatch;
  // Older indexes did not save sampled signatures. A filename collision cannot
  // be resolved by selecting one same-named File without its relative folder.
  const exactDirectory = selectedPath.includes('/') && selectedPath === originalPath;
  if (!exactDirectory && tracks.some(item => item.id !== track.id && item.sourceType === 'local' && leaf(item.fileName || item.externalId || '') === name))
    return '同名文件无法确认所属原目录，请重新选择原文件夹恢复';
  return '';
}
